/**
 * Utility to normalize AI response keys to consistent lowercase format.
 * 
 * The AI may return keys in various formats:
 * - PascalCase: Title, Link, Description, Promotions
 * - camelCase: title, link, description
 * - snake_case: vehicle_count, main_picture
 * 
 * This normalizer converts all keys to lowercase to ensure consistent access.
 */

// Map of known PascalCase/mixed keys to their normalized lowercase equivalents
const KEY_MAPPINGS: Record<string, string> = {
  // Promotions
  'Title': 'title',
  'Link': 'link',
  'Description': 'description',
  'Details': 'details',
  'Image': 'image',
  'Type': 'type',
  
  // Vehicles/Inventory
  'VIN': 'vin',
  'Make': 'make',
  'Model': 'model',
  'Year': 'year',
  'Price': 'price',
  'Mileage': 'mileage',
  'Stock': 'stock',
  'Color': 'color',
  'Version': 'version',
  'Trim': 'trim',
  'VDP_URL': 'vdp_url',
  'Main_Picture': 'main_picture',
  'Main_Picture_URL': 'main_picture_url',
  'Exterior_Color': 'exterior_color',
  'Interior_Color': 'interior_color',
  'Vehicle_Color': 'vehicle_color',
  
  // Reply Options
  'Option': 'option',
  'Option1': 'option1',
  'Option2': 'option2',
  'Option3': 'option3',
  'Option4': 'option4',
  
  // Content fields - keep original case for top-level structure
  // but normalize nested objects
};

// Keys that should preserve their original case (top-level structural keys)
const PRESERVE_CASE_KEYS = new Set([
  'Reply_Message',
  'Reply_Message_Content',
  'Content',
  'Intro',
  'Closing',
  'Inventory',
  'Promotions',
  'Showroom',
  'OEM_Content',
  'Suggested_Models',
  'Reply_Options',
  'Opening_Hours',
  'Vehicle_Count',
  'Inventory_Count',
  'Inventory_Count_By_Category',
  'Window_Sticker',
  'CARFAX_URL',
  'Similar_Vehicles',
  'Proposed_Link',
  'Proposed_Link_CTA',
  'Interest',
  'Form',
  'Contact',
  'Language',
  'Answer',
  'actions',
  'Actions',
]);

/**
 * Normalize a single key based on mappings
 */
function normalizeKey(key: string): string {
  // Check direct mapping first
  if (KEY_MAPPINGS[key]) {
    return KEY_MAPPINGS[key];
  }
  return key;
}

/**
 * Recursively normalize all keys in an object to handle case variations.
 * Creates a new object with both original and normalized key access.
 * 
 * @param obj - The object to normalize
 * @param depth - Current recursion depth (to prevent infinite loops)
 * @returns Normalized object with lowercase key access
 */
export function normalizeAIResponseKeys<T>(obj: T, depth = 0): T {
  // Prevent infinite recursion
  if (depth > 20) {
    return obj;
  }

  // Handle null/undefined
  if (obj === null || obj === undefined) {
    return obj;
  }

  // Handle arrays - normalize each element
  if (Array.isArray(obj)) {
    return obj.map(item => normalizeAIResponseKeys(item, depth + 1)) as T;
  }

  // Handle non-objects (primitives)
  if (typeof obj !== 'object') {
    return obj;
  }

  // Handle Date objects
  if (obj instanceof Date) {
    return obj;
  }

  // Create a new object with normalized keys
  const normalized: Record<string, any> = {};
  
  for (const [key, value] of Object.entries(obj as Record<string, any>)) {
    // Keep original key
    normalized[key] = normalizeAIResponseKeys(value, depth + 1);
    
    // Add lowercase version if different (for case-insensitive access)
    const lowerKey = key.toLowerCase();
    if (lowerKey !== key && !normalized.hasOwnProperty(lowerKey)) {
      normalized[lowerKey] = normalized[key];
    }
    
    // Add mapped version if exists
    const mappedKey = normalizeKey(key);
    if (mappedKey !== key && !normalized.hasOwnProperty(mappedKey)) {
      normalized[mappedKey] = normalized[key];
    }
  }

  return normalized as T;
}

/**
 * Normalize specific nested objects that commonly have case issues.
 * This is more targeted and efficient than full recursive normalization.
 * 
 * @param response - The parsed AI response
 * @returns Response with normalized nested objects
 */
export function normalizeKnownNestedObjects(response: any): any {
  if (!response || typeof response !== 'object') {
    return response;
  }

  const result = { ...response };

  // Normalize Promotions array
  const promotions = result.Reply_Message?.Content?.Promotions || 
                     result.Content?.Promotions ||
                     result.Promotions;
  
  if (Array.isArray(promotions)) {
    const normalizedPromotions = promotions.map(promo => {
      if (!promo || typeof promo !== 'object') return promo;
      return {
        // Keep original keys for backwards compatibility
        ...promo,
        // Add normalized lowercase versions
        title: promo.title || promo.Title || '',
        link: promo.link || promo.Link || '',
        description: promo.description || promo.Description || '',
        details: promo.details || promo.Details || '',
        image: promo.image || promo.Image || '',
        type: promo.type || promo.Type || '',
      };
    });

    // Update the promotions in the response
    if (result.Reply_Message?.Content?.Promotions) {
      result.Reply_Message.Content.Promotions = normalizedPromotions;
    } else if (result.Content?.Promotions) {
      result.Content.Promotions = normalizedPromotions;
    } else if (result.Promotions) {
      result.Promotions = normalizedPromotions;
    }
  }

  // Normalize Inventory array
  const inventory = result.Reply_Message?.Content?.Inventory || 
                    result.Content?.Inventory ||
                    result.Inventory;
  
  if (Array.isArray(inventory)) {
    const normalizedInventory = inventory.map(vehicle => {
      if (!vehicle || typeof vehicle !== 'object') return vehicle;
      return {
        ...vehicle,
        vin: vehicle.vin || vehicle.VIN || vehicle.Vin || '',
        make: vehicle.make || vehicle.Make || '',
        model: vehicle.model || vehicle.Model || '',
        year: vehicle.year || vehicle.Year || '',
        price: vehicle.price || vehicle.Price || '',
        mileage: vehicle.mileage || vehicle.Mileage || '',
        stock: vehicle.stock || vehicle.Stock || '',
        trim: vehicle.trim || vehicle.Trim || vehicle.version || vehicle.Version || '',
        vdp_url: vehicle.vdp_url || vehicle.VDP_URL || vehicle.Vdp_Url || '',
        main_picture: vehicle.main_picture || vehicle.Main_Picture || vehicle.Main_Picture_URL || vehicle.main_picture_url || '',
        exterior_color: vehicle.exterior_color || vehicle.Exterior_Color || vehicle.Vehicle_Color || vehicle.vehicle_color || vehicle.color || vehicle.Color || '',
      };
    });

    if (result.Reply_Message?.Content?.Inventory) {
      result.Reply_Message.Content.Inventory = normalizedInventory;
    } else if (result.Content?.Inventory) {
      result.Content.Inventory = normalizedInventory;
    } else if (result.Inventory) {
      result.Inventory = normalizedInventory;
    }
  }

  // Normalize Reply_Options array
  const replyOptions = result.Reply_Message?.Content?.Reply_Options || 
                       result.Content?.Reply_Options ||
                       result.Reply_Options;
  
  if (Array.isArray(replyOptions)) {
    const normalizedOptions = replyOptions.map(opt => {
      if (typeof opt === 'string') return opt;
      if (!opt || typeof opt !== 'object') return opt;
      // Handle { Option: \"value\" } format
      return opt.option || opt.Option || opt.value || opt.Value || opt;
    });

    if (result.Reply_Message?.Content?.Reply_Options) {
      result.Reply_Message.Content.Reply_Options = normalizedOptions;
    } else if (result.Content?.Reply_Options) {
      result.Content.Reply_Options = normalizedOptions;
    } else if (result.Reply_Options) {
      result.Reply_Options = normalizedOptions;
    }
  }

  // Normalize Suggested_Models array
  const suggestedModels = result.Reply_Message?.Content?.Showroom?.Suggested_Models ||
                          result.Content?.Showroom?.Suggested_Models ||
                          result.Showroom?.Suggested_Models;
  
  if (Array.isArray(suggestedModels)) {
    const normalizedModels = suggestedModels.map(model => {
      if (!model || typeof model !== 'object') return model;
      return {
        ...model,
        name: model.name || model.Name || model.model || model.Model || '',
        styleId: model.styleId || model.StyleId || model.style_id || model.Style_Id || '',
        vdp_url: model.vdp_url || model.VDP_URL || model.Vdp_Url || model.link || model.Link || '',
        main_picture_url: model.main_picture_url || model.Main_Picture_URL || model.Main_Picture || model.main_picture || model.image || model.Image || '',
      };
    });

    if (result.Reply_Message?.Content?.Showroom?.Suggested_Models) {
      result.Reply_Message.Content.Showroom.Suggested_Models = normalizedModels;
    } else if (result.Content?.Showroom?.Suggested_Models) {
      result.Content.Showroom.Suggested_Models = normalizedModels;
    } else if (result.Showroom?.Suggested_Models) {
      result.Showroom.Suggested_Models = normalizedModels;
    }
  }

  // Normalize Opening_Hours array
  const openingHours = result.Reply_Message?.Content?.Opening_Hours ||
                       result.Content?.Opening_Hours ||
                       result.Opening_Hours;
  
  if (Array.isArray(openingHours)) {
    const normalizedHours = openingHours.map(hours => {
      if (!hours || typeof hours !== 'object') return hours;
      return {
        ...hours,
        day: hours.day || hours.Day || '',
        hours: hours.hours || hours.Hours || hours.time || hours.Time || '',
        open: hours.open || hours.Open || '',
        close: hours.close || hours.Close || '',
      };
    });

    if (result.Reply_Message?.Content?.Opening_Hours) {
      result.Reply_Message.Content.Opening_Hours = normalizedHours;
    } else if (result.Content?.Opening_Hours) {
      result.Content.Opening_Hours = normalizedHours;
    } else if (result.Opening_Hours) {
      result.Opening_Hours = normalizedHours;
    }
  }

  // Normalize Vehicle_Count / Inventory_Count arrays
  const vehicleCount = result.Reply_Message?.Content?.Vehicle_Count ||
                       result.Content?.Vehicle_Count ||
                       result.Vehicle_Count ||
                       result.Reply_Message?.Content?.Inventory_Count ||
                       result.Content?.Inventory_Count ||
                       result.Inventory_Count;
  
  if (Array.isArray(vehicleCount)) {
    const normalizedCount = vehicleCount.map(item => {
      if (!item || typeof item !== 'object') return item;
      return {
        ...item,
        category: item.category || item.Category || '',
        count: item.count || item.Count || item.total || item.Total || 0,
        color: item.color || item.Color || '',
      };
    });

    // Update in all possible locations
    if (result.Reply_Message?.Content?.Vehicle_Count) {
      result.Reply_Message.Content.Vehicle_Count = normalizedCount;
    }
    if (result.Reply_Message?.Content?.Inventory_Count) {
      result.Reply_Message.Content.Inventory_Count = normalizedCount;
    }
    if (result.Content?.Vehicle_Count) {
      result.Content.Vehicle_Count = normalizedCount;
    }
    if (result.Content?.Inventory_Count) {
      result.Content.Inventory_Count = normalizedCount;
    }
    if (result.Vehicle_Count) {
      result.Vehicle_Count = normalizedCount;
    }
    if (result.Inventory_Count) {
      result.Inventory_Count = normalizedCount;
    }
  }

  return result;
}

/**
 * Main normalization function - combines both approaches.
 * Use this at the main parsing entry points.
 */
export function normalizeAIResponse(response: any): any {
  if (!response || typeof response !== 'object') {
    return response;
  }

  // First, normalize known nested objects (more targeted, handles specific arrays)
  const withNormalizedNested = normalizeKnownNestedObjects(response);
  
  return withNormalizedNested;
}
